/**
 * Textos (es) de planta — OBR-149: recompte d'existències a l'App: namespace `planta.stockCount`.
 * Tall propi: no es toca `ca.ts`. Les tres llengües han de tenir les mateixes claus (i18n.test.ts) i les mateixes
 * variables. Vocabulari del sector: «Existències», «recompte», mai «Stock».
 */

/**
 * Tipus AMPLE a posta (no `as const`): el tipus literal de TOTS els catàlegs junts passa del que `tsc` pot serialitzar a la declaració
 * (TS7056 a `i18n/index.ts`) i només aquest espai en porta 139 claus. Les claus es comproven igualment: `i18n.test.ts` (les tres llengües
 * amb les mateixes claus i variables) i `i18nUsage.test.ts` (cada `t('planta.stockCount:…')` existeix). Quan l'anotació explícita de
 * `resources` (commit 64cd075, branca del catàleg) sigui a main, es pot tornar a `as const`.
 */
export const plantaCountEs: { readonly 'planta.stockCount': Readonly<Record<string, string>> } = {
  'planta.stockCount': {
    // ── Entrada i llista de recomptes ─
    title: 'Recuento de existencias',
    subtitle: 'Cuenta lo que hay en el obrador. El responsable valida el recuento en el BackOffice.',
    entryTitle: 'Recuento de existencias',
    entryBody: 'Escanea la etiqueta del lote y cuenta lo que hay.',
    entryOpen_one: '{{count}} recuento abierto',
    entryOpen_other: '{{count}} recuentos abiertos',
    entryNone: 'Ningún recuento abierto',
    entryUnknown: 'Comprueba los recuentos abiertos',
    listOpenTitle: 'Recuentos abiertos',
    listClosedTitle: 'Cerrados',
    emptyOpen: 'No hay ningún recuento abierto',
    emptyOpenHint: 'Cuando el responsable cree uno en el BackOffice, aparecerá aquí.',
    emptyWeb: 'El recuento se hace desde la app del obrador (tableta o móvil).',
    refresh: 'Actualiza',
    refreshing: 'Actualizando…',
    refreshOffline: 'Sin conexión: se muestra la última copia.',
    refreshFailed: 'No se ha podido actualizar: se muestra la última copia.',
    refreshUnsynced: 'Hay conteos pendientes de enviar: se envían primero y después se actualiza.',
    refreshPartial: 'Algunos datos no se han podido leer y se han dejado fuera.',
    scopeTenant: 'Todo el obrador',
    scopeNamed_zone: 'Zona · {{name}}',
    scopeNamed_section: 'Sección · {{name}}',
    scopeNamed_location: 'Ubicación · {{name}}',
    scopeNamed_article: 'Artículo · {{name}}',
    scopeType_zone: 'Una zona',
    scopeType_section: 'Una sección',
    scopeType_location: 'Una ubicación',
    scopeType_article: 'Un artículo',
    countProgress: '{{counted}} de {{total}} líneas contadas',
    countNotLoaded: 'Ábrelo para bajar las líneas.',
    photoOn: 'Foto del {{date}}',
    statusOpen: 'Abierto',
    status_validated: 'Validado',
    status_cancelled: 'Cancelado',
    closedOn: 'Cerrado el {{date}}',
    unsyncedBadge_one: '{{count}} pendiente de enviar',
    unsyncedBadge_other: '{{count}} pendientes de enviar',
    rejectedBadge_one: '{{count}} no aplicado',
    rejectedBadge_other: '{{count}} no aplicados',
    openMore_one: 'Hay {{count}} recuento abierto más que no se ve aquí. Actualiza o cierra alguno para verlo.',
    openMore_other: 'Hay {{count}} recuentos abiertos más que no se ven aquí. Actualiza o cierra alguno para verlos.',
    openCountLabel: 'Abre el recuento {{number}}',
    // ── Pantalla d’un recompte ─
    back: 'Vuelve a los recuentos',
    missingCount: 'Este recuento no está en el dispositivo. Vuelve a la lista y actualiza.',
    pendingValidation: 'Pendiente de validar: cuando acabes, el responsable lo valida en el BackOffice. Aquí no se ajusta nada.',
    readOnlyValidated: 'Este recuento ya se ha validado en el BackOffice. Solo se puede consultar.',
    readOnlyCancelled: 'Este recuento se ha cancelado. Solo se puede consultar.',
    unsyncedNotice_one: '{{count}} conteo pendiente de enviar. Se enviará solo al recuperar la conexión.',
    unsyncedNotice_other: '{{count}} conteos pendientes de enviar. Se enviarán solos al recuperar la conexión.',
    rejectedNotice_one: '{{count}} conteo no aplicado: el servidor no ha podido registrarlo (el recuento ya no está abierto o el lote no encaja). La cantidad se mantiene aquí.',
    rejectedNotice_other: '{{count}} conteos no aplicados: el servidor no ha podido registrarlos (el recuento ya no está abierto o el lote no encaja). Las cantidades se mantienen aquí.',
    rejectedAck: 'Entendido',
    truncatedNotice: 'Este recuento tiene más líneas de las que guarda el dispositivo ({{count}}). Cuenta el resto desde el BackOffice.',
    linesFrom: 'Líneas bajadas el {{date}}',
    neverPulled: 'Todavía no se han bajado las líneas de este recuento. Hace falta conexión para bajarlas.',
    loadingLines: 'Bajando las líneas…',
    noLines: 'Este recuento no tiene ninguna línea.',
    scan: 'Escanea',
    addFound: 'Añade un lote encontrado',
    scanTitle: 'Escanea la etiqueta del lote',
    scanHint: 'Apunta al QR de la etiqueta.',
    filterLabel: 'Filtra las líneas',
    filterAll: 'Todas · {{count}}',
    filterPending: 'Pendientes · {{count}}',
    filterShort: 'Falta · {{count}}',
    filterOver: 'Sobra · {{count}}',
    filterMatch: 'Coincide · {{count}}',
    searchPlaceholder: 'Busca por artículo, lote o lugar',
    searchLabel: 'Busca una línea',
    resultsCount_one: '{{count}} línea',
    resultsCount_other: '{{count}} líneas',
    emptyNoMatch: 'Ninguna línea coincide con «{{query}}».',
    emptyFilter: 'Ninguna línea en este estado.',
    showMore: 'Muestra más',
    resultsShown: 'Se muestran {{shown}} de {{total}}: busca o filtra para encontrar otras.',
    // ── Una línia ─
    lotLine: 'Lote {{lot}}',
    noLot: 'Sin lote',
    newLot: 'Lote nuevo',
    newLotWithCode: 'Lote nuevo · {{code}}',
    noPlace: 'Sin lugar',
    expiresOn: 'Caduca {{date}}',
    photoQty: 'Foto ({{date}}): {{quantity}}',
    countedQty: 'Contado: {{quantity}}',
    notCounted: 'Sin contar',
    foundTag: 'Encontrado',
    unsyncedTag: 'Pendiente de enviar',
    rejectedTag: 'No aplicado',
    state_pending: 'Pendiente',
    state_match: 'Coincide',
    state_short: 'Falta {{quantity}}',
    state_over: 'Sobra {{quantity}}',
    lineLabel: '{{article}}, {{state}}',
    // ── El full de quantitat ─
    sheetTitle: 'Cuenta',
    sheetPhoto: 'Foto del {{date}}: {{quantity}}',
    sheetFound: 'Lote encontrado: no estaba en la foto.',
    sheetCurrent: 'Ya contado: {{quantity}}',
    quantityLabel: 'Cantidad contada',
    matchButton: 'Coincide',
    matchHelp: 'Cuenta lo mismo que dice la foto.',
    save: 'Guarda',
    modeSet: 'Sustituye',
    modeAdd: 'Suma',
    modeAddTotal: 'Total: {{quantity}}',
    invalidQuantity: 'Escribe una cantidad válida.',
    conflictTitle: 'Otra persona ya lo había contado',
    conflictBody: 'Hay {{counted}} contados por otra persona. ¿Qué quieres hacer con los {{typed}} que has escrito?',
    conflictAdd: 'Suma (total {{total}})',
    conflictReplace: 'Sustituye ({{typed}})',
    conflictCancel: 'Cancela',
    placesTitle: '¿Dónde está este lote?',
    placesBody: 'El lote {{lot}} está en más de un lugar de este recuento. Elige dónde lo cuentas.',
    // ── Un lot trobat ─
    foundTitle: 'Añade un lote encontrado',
    foundBody: 'Un lote que no estaba en la foto del recuento.',
    foundScan: 'Escanea una etiqueta',
    foundNew: 'Lote nuevo (sin etiqueta)',
    foundNoLine: 'El lote {{lot}} no está en la lista de este recuento.',
    foundAddIt: 'Lo añado como lote encontrado',
    foundOutOfScope: 'El lote {{lot}} es de {{article}}, pero está fuera del alcance de este recuento y no se puede añadir.',
    foundKnown: '{{article}} · lote {{lot}}',
    foundWhere: '¿Dónde lo has encontrado?',
    foundUnknown: 'No conocemos el lote {{lot}}. Se creará uno nuevo: elige el artículo.',
    knownLotsChecking: 'Comprobando el lote {{lot}}…',
    knownLotsCannotTitle: 'No puedo comprobar si el lote ya existe',
    knownLotsCannotBody: 'El lote {{lot}} no está en la lista que tiene el dispositivo y no puedo saber si ya existe (sin conexión, lista antigua o incompleta). Puedes volver a probar con conexión o contarlo como nuevo igualmente: el número escaneado se guarda en la nota y el responsable lo revisará al validar.',
    knownLotsCountAsNew: 'Cuéntalo como nuevo igualmente',
    knownLotsCountAsNewHelp: 'El responsable lo revisará al validar.',
    knownLotsRetry: 'Volver a probar',
    scannedLotNote: 'Lote escaneado: {{lot}}',
    newLotTitle: 'Lote nuevo',
    newLotArticle: 'Artículo',
    newLotPick: 'Elige el artículo',
    newLotSearch: 'Busca un artículo por nombre o código',
    newLotNoArticles: 'Este dispositivo todavía no tiene artículos. Conéctalo y actualiza.',
    newLotNoMatch: 'Ningún artículo coincide con «{{query}}».',
    newLotChange: 'Cambia el artículo',
    newLotSupplierCode: 'Código de lote del proveedor (opcional)',
    newLotExpiry: 'Caducidad',
    newLotExpiryHelp: 'Este artículo pide fecha de caducidad.',
    newLotQuantity: 'Cantidad encontrada',
    newLotHelp: 'El número de lote de Obrava lo crea el servidor al validar el recuento: no hace falta escribirlo.',
    newLotSave: 'Añade el lote',
    issue_untracked: 'Este artículo no lleva control de existencias: no hay nada que contar.',
    issue_production_article: 'Este artículo se produce: no se le puede crear un lote nuevo desde un recuento. Avisa al responsable.',
    issue_quantity: 'Escribe una cantidad mayor que cero.',
    issue_expiry_required: 'Falta la fecha de caducidad de este artículo.',
    // ── Missatges ─
    savedLine: 'Contado: {{article}} · {{quantity}}',
    savedMerged: 'Ya había una línea de este lote: se ha sumado. {{article}} · +{{quantity}} (total {{total}})',
    errScan: 'No se ha podido leer la etiqueta. Vuelve a apuntarla.',
    errNotOpen: 'Este recuento ya no está abierto: no se puede contar.',
    errProvisionalNewLot: 'Este lote nuevo todavía no se ha enviado. Espera a que se sincronice para cambiar la cantidad.',
    errStaleLine: 'Esta línea se ha actualizado mientras la contabas. Cierra la hoja y vuelve a abrirla.',
    errSave: 'No se ha podido guardar el conteo. Inténtalo de nuevo.',
    cannotCount: 'Tu rol no puede contar: puedes consultar el recuento, pero no guardar cantidades.',
  },
};
